import { CC } from "@bigspring/core-components";
import { Company } from "@shared/Collections/models/company";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppIcon, ExchangeIcon, MenuButton, Section } from "../../styles";
import { BaseMenu, MenuOption, Props as BaseMenuProps } from "../BaseMenu";
import { CompanySelector, PinnedMenuItem } from "../BaseMenu/styles";
import { CreateCompanyButton } from "./styles";
import { RevampedNavItem } from "../../menuOptions";

const PINNED_COMPANIES_KEY = "pinned-companies";

function PinIcon({ filled }: { filled?: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

export function clearPinnedCompanies() {
  localStorage.removeItem(PINNED_COMPANIES_KEY);
}

export type Props<Option extends MenuOption> = BaseMenuProps & {
  options: Option[][];
  selectedOption?: Option & { uri?: string };
  companies: Company[];
  selectedCompany?: Company;
  isNewAdminNav: boolean;
  revampedMenuOptions: RevampedNavItem[];
  logoutClick: () => void;
  onCreateCompanyClick: () => void;
  onRevampedOptionClick: (option: RevampedNavItem) => Promise<void>;
  onOptionClick: (option: Option) => Promise<void>;
  onCompanyChange: (company: Company) => Promise<void>;
};

export function SuperAdminMenu<Option extends MenuOption>({
  label,
  options,
  selectedOption,
  selectedCompany,
  companies,
  isNewAdminNav,
  revampedMenuOptions,
  logoutClick,
  onCreateCompanyClick,
  onOptionClick,
  onRevampedOptionClick,
  onCompanyChange,
  onOpenMenuClick,
  onCloseMenuClick,
}: Props<Option>) {
  const optionsWithoutSuperAdminOptions = useMemo(
    () => options.filter((option, index) => index > 0),
    [options]
  );

  const [accordionOpen, setAccordionOpen] = useState(1);

  // Pinned companies state
  const [pinnedCompanyIds, setPinnedCompanyIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(PINNED_COMPANIES_KEY);

      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const togglePinCompany = useCallback(
    (companyId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setPinnedCompanyIds((prev) => {
        const newPinned = prev.includes(companyId)
          ? prev.filter((id) => id !== companyId)
          : [...prev, companyId];
        localStorage.setItem(PINNED_COMPANIES_KEY, JSON.stringify(newPinned));

        return newPinned;
      });
    },
    []
  );

  // Sort companies with pinned ones first
  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const aIsPinned = pinnedCompanyIds.includes(a.id);
      const bIsPinned = pinnedCompanyIds.includes(b.id);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      return a.name.localeCompare(b.name);
    });
  }, [companies, pinnedCompanyIds]);

  const handleAccordionOpen = (panel: number) => {
    setAccordionOpen(accordionOpen === panel ? 0 : panel);
  };

  useEffect(() => {
    // find the index of the selected option in the revamped menu options
    const selectedOptionIndex = revampedMenuOptions.findIndex((option) =>
      option.subsections?.find(
        (subsection) => subsection.href === selectedOption?.uri
      )
    );
    setAccordionOpen(selectedOptionIndex + 1);
  }, [selectedOption, revampedMenuOptions]);

  if (isNewAdminNav) {
    return (
      <BaseMenu
        label={label}
        onOpenMenuClick={onOpenMenuClick}
        onCloseMenuClick={onCloseMenuClick}
        isNewAdminNav={isNewAdminNav}
      >
        <Section item container xs={12} direction="column" $borderBottom>
          <CC.FloatMenu
            color="primary"
            element={
              <CompanySelector
                color="utility"
                text={selectedCompany?.name}
                endIcon={<ExchangeIcon />}
                $isNewAdminNav={true}
              />
            }
            showFilter
            optionsHeader={
              <CreateCompanyButton
                type="button"
                color="lightblue"
                startIcon={<CC.AddCircleOutlineIcon $large />}
                text="Create Company"
                onClick={onCreateCompanyClick}
              />
            }
            defaultValue={selectedCompany?.id}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            options={sortedCompanies}
            onChange={onCompanyChange}
            renderOption={(option, { selected, onClick }) => {
              const isPinned = pinnedCompanyIds.includes(option.id);

              return (
                <PinnedMenuItem
                  key={option.id}
                  $color="primary"
                  selected={selected}
                  onClick={onClick}
                >
                  <span className="company-name">{option.name}</span>
                  <span
                    className={`pin-icon ${isPinned ? "pinned" : ""}`}
                    onClick={(e) => togglePinCompany(option.id, e)}
                    title={isPinned ? "Unpin company" : "Pin company"}
                  >
                    <PinIcon filled={isPinned} />
                  </span>
                  {selected && <CC.CheckIcon />}
                </PinnedMenuItem>
              );
            }}
          />
        </Section>
        <CC.Box mt={2}>
          {revampedMenuOptions.map((item, index) => {
            const contentHeight = (item.subsections?.length || 0) * 44;

            return (
              <CC.Box
                sx={{
                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor:
                      accordionOpen === index + 1
                        ? "transparent"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                key={item.text}
                id={item.id}
                onClick={() => {
                  if (item.subsections?.length) {
                    handleAccordionOpen(index + 1);
                  } else {
                    void onRevampedOptionClick(item);
                  }
                }}
              >
                <CC.Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  height="44px"
                  marginLeft={"20px"}
                >
                  {item.icon || item.iconFilled || item.iconOutline}
                  {item.text}
                  <CC.Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
                    {item.subsections?.length ? (
                      <CC.ChevronRightIcon
                        sx={{
                          transform:
                            accordionOpen === index + 1
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                          transition: "transform 0.4s ease",
                        }}
                      />
                    ) : null}
                  </CC.Box>
                </CC.Box>
                <CC.Box
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    transition: "max-height 0.4s ease",
                    maxHeight:
                      accordionOpen === index + 1
                        ? `${contentHeight}px`
                        : "0px",
                  }}
                >
                  {item.subsections?.map((subsection) => (
                    <CC.Box
                      key={subsection.text}
                      id={subsection.id}
                      onClick={async (e) => {
                        e.stopPropagation();
                        await onRevampedOptionClick(subsection);
                      }}
                      sx={{
                        height: "44px",
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "52px",
                        color:
                          selectedOption?.uri === subsection.href
                            ? "white"
                            : "#6E6E6B",
                        backgroundColor:
                          selectedOption?.uri === subsection.href
                            ? "#E50063"
                            : "transparent",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: "#E50063",
                          color: "white",
                        },
                      }}
                    >
                      {subsection.text}
                    </CC.Box>
                  ))}
                </CC.Box>
              </CC.Box>
            );
          })}
        </CC.Box>
        {/* at the bottom of the menu */}
        <CC.Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <MenuButton
            text="App"
            color="admin-navigation"
            onClick={() =>
              void window.open("https://app.bigspring.io", "_blank")
            }
            startIcon={<AppIcon />}
          />
          <MenuButton
            text="Logout"
            color="admin-navigation"
            onClick={logoutClick}
            startIcon={<CC.LogoutIcon />}
          />
        </CC.Box>
      </BaseMenu>
    );
  }

  return (
    <BaseMenu
      label={label}
      onOpenMenuClick={onOpenMenuClick}
      onCloseMenuClick={onCloseMenuClick}
      isNewAdminNav={isNewAdminNav}
    >
      <Section
        item
        container
        xs={12}
        direction="column"
        $borderBottom
        mt="10px"
      >
        <CC.FloatMenu
          color="primary"
          element={
            <CompanySelector
              color="utility"
              text={selectedCompany?.name}
              endIcon={<CC.ChevronDownIcon />}
            />
          }
          showFilter
          optionsHeader={
            <CreateCompanyButton
              type="button"
              color="lightblue"
              startIcon={<CC.AddCircleOutlineIcon $large />}
              text="Create Company"
              onClick={onCreateCompanyClick}
            />
          }
          defaultValue={selectedCompany?.id}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          options={companies}
          onChange={onCompanyChange}
        />
        <CC.Grid item xs={12} container direction="column">
          {options[0].map((option) => (
            <CC.Grid key={option.text} item>
              <MenuButton
                text={option.text}
                color="admin-navigation"
                startIcon={option.startIcon(selectedOption === option)}
                selected={selectedOption === option}
                onClick={() => onOptionClick(option)}
              />
            </CC.Grid>
          ))}
        </CC.Grid>
      </Section>

      {optionsWithoutSuperAdminOptions.map((section, index) => (
        <Section
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          item
          xs={12}
          container
          direction="column"
          $borderBottom={index + 1 < optionsWithoutSuperAdminOptions.length}
        >
          {section.map((option) => (
            <CC.Grid key={option.text} item>
              <MenuButton
                text={option.text}
                color="admin-navigation"
                startIcon={option.startIcon(selectedOption === option)}
                selected={selectedOption === option}
                onClick={() => onOptionClick(option)}
              />
            </CC.Grid>
          ))}
        </Section>
      ))}
    </BaseMenu>
  );
}
